import React from "react";
import { Box, Typography } from '@mui/material';
import { sources } from "../../../../utils/SourceUtil";
import { getSenioriteFromDate } from "../../../../utils/FunctionHelpers";
import { contratLabel, parseContrats } from "../../../../utils/ContratUtil";
import LinkedIn from '@mui/icons-material/LinkedIn';
import "../visuialisation.css";
import moment from "moment";

const getDisplayValue = (value) => {
  if (React.isValidElement(value)) return value;
  if ([null, undefined, "", "null"].includes(value)) return "-";
  if (Array.isArray(value)) return value.map(getDisplayValue).join(", ");
  if (typeof value === "object") {
    if (value.name) return value.name;
    if (value.nom || value.prenom) return `${value.prenom || ""} ${value.nom || ""}`.trim();
    if (value.label) return value.label;
    return "-";
  }
  return value;
};

const getSourceValue = (source) => {
  if ([null, undefined, "", "0", "null"].includes(source)) return "-";
  if (typeof source === "object") return getDisplayValue(source);
  return sources.find(item => item.id == source)?.name || "-";
};

// Contrat can hold several values ("CDI,Freelance"): show them all.
const getStatusValue = (status) => {
  if (status && typeof status === "object" && !Array.isArray(status)) return getDisplayValue(status);
  const values = parseContrats(status);
  return values.length ? values.map(contratLabel).join(", ") : "-";
};

function FieldRow({ label, value, block = false }) {
  const displayValue = getDisplayValue(value);
  const isEmpty = displayValue === '-';
  const stacked = block && !isEmpty;
  return (
    // Container = the row itself: when the card is narrow (side panel, mobile)
    // the value goes under its label and reads as a normal paragraph.
    <Box sx={{ containerType: 'inline-size', '&:last-child > div': { borderBottom: 'none' } }}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: stacked ? 'stretch' : 'flex-start',
        py: 1,
        px: 0.75,
        gap: stacked ? 0.5 : 2,
        borderBottom: '1px solid #f3f4f6',
        borderRadius: '4px',
        transition: 'background 0.12s',
        '&:hover': { backgroundColor: '#f8fafc' },
        '@container (max-width: 340px)': {
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 0.25,
          '& > .field-value': { textAlign: 'left' },
        },
      }}
    >
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#9ca3af',
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
          flexShrink: 0,
          minWidth: 108,
          lineHeight: 1.5,
          mt: '1px',
        }}
      >
        {label}
      </Typography>
      <Box
        className="field-value"
        sx={{
          minWidth: 0,
          fontSize: '1rem',
          color: isEmpty ? '#d1d5db' : '#1f2937',
          fontWeight: isEmpty ? 400 : 500,
          fontStyle: isEmpty ? 'italic' : 'normal',
          textAlign: stacked ? 'left' : 'right',
          whiteSpace: stacked ? 'pre-line' : 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: 1.5,
        }}
      >
        {displayValue}
      </Box>
    </Box>
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, mt: 1 }}>
      <Box sx={{ width: 3, height: 16, borderRadius: 2, background: 'linear-gradient(180deg, #03407b, #02357a)', flexShrink: 0 }} />
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#03407b', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        {children}
      </Typography>
    </Box>
  );
}

function Informations({
  linkedin,
  email,
  telephone,
  employeur,
  fonction,
  dateDebutExperience,
  sachezQue,
  consultant,
  sourceur,
  avatar,
  accountManager,
  competenceSectorielle,
  competenceMetier,
  competencesTechniques,
  ecole,
  source,
  anglais,
  francais,
  createdAt,
  status,
  localisation,
}) {
  const contactRows = [
    { label: "Téléphone", value: telephone },
    { label: "Email", value: email },
    {
      label: "LinkedIn",
      value: linkedin ? (
        <a href={linkedin} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', color: '#0077b5' }}>
          <LinkedIn sx={{ fontSize: 20 }} />
        </a>
      ) : "-",
    },
    { label: "Delivery Manager", value: accountManager ? `${accountManager.prenom} ${accountManager.nom}` : "-" },
    { label: "Talent Manager", value: consultant ? `${consultant.prenom} ${consultant.nom}` : "-" },
    { label: "Sourceur", value: sourceur ? `${sourceur.prenom} ${sourceur.nom}` : "-" },
    { label: "Sachez que", value: sachezQue, block: true },
    { label: "Créé le", value: createdAt ? moment(createdAt).locale("fr").format("DD MMMM YYYY - HH:mm") : "-" },
    { label: "Source", value: getSourceValue(source) },
    ...(source == 1 || source == 6 ? [{ label: "Avatar", value: avatar ? `${avatar.prenom} ${avatar.nom}` : "-" }] : []),
    { label: "Contrat", value: getStatusValue(status) },
    { label: "Localisation", value: localisation ? localisation.name : "-" },
  ];

  const emploiRows = [
    { label: "Société", value: employeur ? employeur.nomSociete?.toUpperCase() : "-" },
    { label: "Fonction", value: fonction },
    { label: "Séniorité", value: getSenioriteFromDate(dateDebutExperience) ?? "-" },
    {
      label: "Comp. Sect.",
      value: competenceSectorielle?.length ? competenceSectorielle.map(c => c.secteur).join(", ") : "-",
    },
    {
      label: "Comp. Métiers",
      value: competenceMetier?.length ? competenceMetier.map(c => c.metier).join(", ") : "-",
    },
    { label: "Comp. Tech.", value: competencesTechniques !== "null" ? competencesTechniques : "-", block: true },
    { label: "Français", value: francais !== "null" ? francais : "-" },
    { label: "Anglais", value: anglais !== "null" ? anglais : "-" },
    { label: "Diplôme(s)", value: ecole?.name || "-" },
  ];

  return (
    <Box>
      <SectionLabel>Contact</SectionLabel>
      {contactRows.map((row, i) => <FieldRow key={i} label={row.label} value={row.value} block={row.block} />)}
      <Box sx={{ mt: 1.5 }}>
        <SectionLabel>Emploi</SectionLabel>
        {emploiRows.map((row, i) => <FieldRow key={i} label={row.label} value={row.value} block={row.block} />)}
      </Box>
    </Box>
  );
}

export default Informations;
